import React, { useRef } from 'react';
import { motion } from 'framer-motion';
import { Term } from '../../Data/types';
import { ParticleCanvas } from '../ParticleCanvas/ParticleCanvas';

interface TermTileProps {
  term: Term;
  isRecentlyUnlocked?: boolean;
  isDropTarget?: boolean;
  isDragging?: boolean;
  showHint?: boolean;
  onDragEnd: (termId: string, x: number, y: number) => void;
  onDragUpdate?: (termId: string, x: number, y: number) => void;
  onDragStart?: (termId: string) => void;
  onTap?: (termId: string) => void;
}

export const TermTile: React.FC<TermTileProps> = ({
  term,
  isRecentlyUnlocked = false,
  isDropTarget = false,
  isDragging = false,
  showHint = false,
  onDragEnd,
  onDragUpdate,
  onDragStart,
  onTap,
}) => {

  if (!term.isUnlocked) {
    const isTech = term.termType === 'technology';
    return (
      <div
        data-term-id={term.termId}
        style={{
          padding: '10px 14px',
          height: 90,
          borderRadius: 10,
          background: isTech ? '#19130a' : '#1a1a2e',
          border: `1px solid ${isTech ? '#78350f' : '#2d2d44'}`,
          color: '#4a4a6a',
          textAlign: 'center',
          cursor: 'default',
          userSelect: 'none',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          filter: 'grayscale(50%) blur(1px)',
        }}
      >
        <span style={{ fontSize: 22, opacity: 0.55, filter: 
            showHint ? 'grayscale(100%)' : 'grayscale(100%) blur(5px)' }}>
            {term.emoji}
        </span>
      </div>
    );
  }

  const isTech = term.termType === 'technology';
  const didDragRef = useRef(false);

  if (!term.isMastered) {
    return (
      <div
        data-term-id={term.termId}
        onClick={() => onTap?.(term.termId)}
        style={{
          padding: '10px 14px',
          height: 90,
          borderRadius: 10,
          background: isTech ? '#1f1200' : '#1e1e3a',
          border: `1px solid ${isTech ? 'rgba(217,119,6,0.6)' : 'rgba(217,119,6,0.6)'}`,
          color: isTech ? '#f59e0b' : '#e2e8f0',
          textAlign: 'center',
          cursor: 'pointer',
          userSelect: 'none',
          width: '100%',
          boxSizing: 'border-box',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 13,
          fontWeight: 500,
        }}
      >
        <span style={{ fontSize: 23, display: 'block', marginBottom: 2, filter: 'blur(4px) grayscale(60%)' }}>
          {term.emoji}
        </span>
        <span>{term.displayName}</span>
      </div>
    );
  }

  return (
    <motion.div
      data-term-id={term.termId}
      drag
      dragSnapToOrigin
        dragTransition={{ bounceStiffness: 9999, bounceDamping: 9999 }}
      animate={{ scale: isDropTarget ? 1.15 : 1, opacity: isDragging ? 0 : 1 }}
      whileDrag={{ scale: 1.08, zIndex: 999, opacity: 1, boxShadow: '0 8px 24px rgba(99,102,241,0.5)' }}
      whileHover={{ scale: 1.04 }}
      whileTap={{ scale: 0.97 }}
      transition={{ type: 'spring', stiffness: 500, damping: 30 }}
      onDragStart={() => { didDragRef.current = true; onDragStart?.(term.termId); }}
      onDrag={(_event, info) => {
        onDragUpdate?.(term.termId, info.point.x, info.point.y);
      }}
      onDragEnd={(_event, info) => {
        onDragEnd(term.termId, info.point.x, info.point.y);
      }}
      onTap={() => {
        if (!didDragRef.current) onTap?.(term.termId);
        didDragRef.current = false;
      }}
      style={{
        textAlign: 'center',
        cursor: 'grab',
        userSelect: 'none',
        width: '100%',
        height: 90,
        transition: 'background 0.15s, border 0.15s',
        position: 'relative',
        zIndex: 20,
      }}
      layoutId={`term-${term.termId}`}
    >

      <div
        style={{
            width: '100%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            zIndex: 100,
            boxShadow: isDropTarget
              ? '0 0 18px 5px rgba(99, 102, 241, 0.55)'
              : isRecentlyUnlocked ? '0 0 14px 5px rgba(255, 255, 255, 0.55)' : 'none',
            padding: '10px 14px',
            borderRadius: 10,
            background: isDropTarget
              ? (isTech ? '#3a2200' : '#2d2d5a')
              : isTech ? '#1f1200' : '#1e1e3a',
            border: isDropTarget
              ? (isTech ? '2px solid rgba(217, 119, 6, 0.9)' : '2px solid rgba(99, 102, 241, 0.9)')
              : isRecentlyUnlocked
              ? '2px solid rgba(255, 255, 255, 0.9)'
              : isTech
              ? '1px solid #b45309'
              : '1px solid #3d3d5c',
            color: isTech ? '#f59e0b' : '#e2e8f0',
            fontSize: 13,
            fontWeight: 500,
            boxSizing: 'border-box',
        }}>
        <span style={{ fontSize: 23, display: 'block', marginBottom: 2, position: 'relative', zIndex: 1 }}>{term.emoji}</span>
        <span style={{ position: 'relative', zIndex: 1 }}>{term.displayName}</span>
      </div>
      
      {isRecentlyUnlocked && (
        <div style={{ position: 'absolute', inset: '-28px', zIndex: 0, pointerEvents: 'none' }}>
          <ParticleCanvas mode="relative" lifetime="forever" speed={0.5} particleSize={0.7} />
        </div>
      )}
    </motion.div>
  );
};
